import { useState, useEffect, useCallback } from 'react';
import {
  fetchNotifications as fetchNotificationsAPI,
  markNotificationAsRead as markReadAPI,
  markAllNotificationsAsRead as markAllReadAPI,
  NotificationFilters
} from '../../../../services/notificationService';
import { Notification, NotificationResponse } from '../../../../types/notificationTypes';

export function useNotifications(
  initialPage = 1,
  initialLimit = 10
) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [pagination, setPagination] =
    useState<Required<NotificationResponse>['pagination']>({
      total: 0,
      page: initialPage,
      limit: initialLimit
    });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPage = useCallback(
    async (page: number) => {
      setLoading(true);
      setError(null);
      try {
        const filters: NotificationFilters = { page, limit: pagination.limit };
        const resp = await fetchNotificationsAPI(filters);
        const { data, pagination: pag } = resp;

        setNotifications(prev =>
          page === 1 ? data : [...prev, ...data]
        );

        // Only update if pag is defined
        if (pag) {
          setPagination(pag);
        }
      } catch {
        setError('Failed to load notifications');
      } finally {
        setLoading(false);
      }
    },
    [ pagination.limit]
  );

  // Initial load
  useEffect(() => {
    loadPage(initialPage);
  }, [loadPage, initialPage]);

  // Poll every 30s for new notifications (page 1 only)
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const resp = await fetchNotificationsAPI( {
          page: 1,
          limit: pagination.limit
        });
        const fresh = resp.data;

        setNotifications(prev => {
          const existing = new Set(prev.map(n => n._id));
          const newOnes = fresh.filter(n => !existing.has(n._id));
          return newOnes.length ? [...newOnes, ...prev] : prev;
        });

        if (resp.pagination) {
          setPagination(resp.pagination);
        }
      } catch {
        // ignore polling errors
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [pagination.limit]);

  const fetchNextPage = useCallback(() => {
    if (pagination.page * pagination.limit < pagination.total) {
      loadPage(pagination.page + 1);
    }
  }, [pagination, loadPage]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await markReadAPI(id);
      setNotifications(prev =>
        prev.map(n => (n._id === id ? { ...n, isRead: true } : n))
      );
    } catch {
      // ignore
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await markAllReadAPI();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch {
      // ignore
    }
  }, []);

  return {
    notifications,
    pagination,
    loading,
    error,
    fetchNextPage,
    markAsRead,
    markAllAsRead
  };
}
